import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import useSEO from "@/hooks/useSEO";
import { siteStats } from "@/mocks/home";

const editorial = [
  {
    icon: "ri-checkbox-circle-line",
    title: "字段统一",
    text: "姓名、假名、生日、出道年份、事务所、代表作品采用固定字段，方便横向对照与后续检索。",
  },
  {
    icon: "ri-links-line",
    title: "条目互链",
    text: "女优、作品与新闻三类条目互相链接，从任一入口都可以顺着关联继续浏览。",
  },
  {
    icon: "ri-error-warning-line",
    title: "标注演示数据",
    text: "评分、浏览量、收藏状态等交互数据均为站内示例，用于演示功能，不作为真实统计口径。",
  },
];

export default function AboutPage() {
  useSEO({
    title: "关于我们 - 站点定位、联系方式与免责声明 - 日本女优大全",
    description:
      "日本女优资料索引站的定位说明：我们如何整理女优档案、影视作品与新闻动态，以及联系方式、版权声明与免责声明。",
    keywords: "日本女优关于,日本女优资料站,联系方式,免责声明",
  });

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "关于" }]} />

      <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="grid lg:grid-cols-[1.1fr_1fr]">
          <div className="p-6 md:p-10">
            <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">ABOUT US</p>
            <h1 className="mt-2 font-heading text-[28px] font-black leading-tight text-foreground-950 md:text-[34px]">
              关于日本女优资料索引
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              日本女优是一份面向影视爱好者、女优粉丝与行业研究者的资料型站点。我们希望把散落在作品表、访谈与新闻里的信息重新整理成可检索的结构：一位演员从出道年份、所属事务所到代表作品与最近动向，都能在同一个页面里串起来。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              站点内容围绕三个板块展开：<strong className="font-medium text-foreground-950">女优档案</strong>、
              <strong className="font-medium text-foreground-950">影视作品索引</strong>与
              <strong className="font-medium text-foreground-950">新闻动态</strong>
              。我们不做内容聚合之外的延伸，也不提供任何视频播放、下载或交易入口。
            </p>
            <dl className="mt-6 grid grid-cols-3 gap-3">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-md border border-background-200 bg-background-100 px-3 py-3 text-center">
                  <dd className="font-heading text-xl font-black text-primary-700">
                    {stat.value}
                    <span className="ml-0.5 font-label text-[10px] font-normal text-foreground-500">
                      {stat.unit}
                    </span>
                  </dd>
                  <dt className="mt-0.5 font-label text-[10px] text-foreground-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative min-h-[240px] bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Quiet%20editorial%20photograph%20of%20a%20Japanese%20film%20archive%20desk%20with%20stacked%20journals%2C%20index%20cards%20and%20a%20small%20brass%20lamp%2C%20warm%20amber%20light%2C%20deep%20crimson%20folder%20accents%2C%20minimal%20elegant%20still%20life%2C%20high%20detail%20film%20texture%2C%20no%20people&width=900&height=1000&seq=about-editorial-01&orientation=portrait"
              alt="日本女优资料索引站的编辑工作台场景"
              title="日本女优资料索引 编辑与档案整理"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="border-b border-background-200 pb-3 font-heading text-xl font-bold text-foreground-950">
          我们如何整理资料
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {editorial.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">版权声明</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              本站为资料索引与展示型站点，站内的文字介绍、排版结构与示例图片仅用于产品演示。页面中的女优档案、作品条目与新闻内容均为站内示例资料，不对应真实个人资料，也不构成对任何个人或机构的官方说明。
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              如需引用本站的整理结构与字段说明，请注明来源「日本女优资料索引」。涉及第三方名称、作品名称的内容，其相关权利归各自权利人所有。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">免责声明</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              本站仅提供人物与作品的信息索引，不提供视频播放、下载、交易或任何形式的付费内容。站内评分、浏览量、收藏状态等数据为前端演示数据，不代表真实统计结果。
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              资料整理过程中可能存在滞后或疏漏。若发现条目信息有误，欢迎通过下方联系方式反馈，我们会在核实后更新。若权利人认为相关内容需要调整或移除，也可直接联系我们。
            </p>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
            <p className="mt-1.5 font-label text-xs leading-relaxed text-foreground-500">
              资料纠错、内容反馈或合作咨询，都可以通过以下方式联系。
            </p>
            <ul className="mt-4 space-y-3">
              <li>
                <a
                  href="mailto:archive@japan-actress.example"
                  className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 font-label text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  archive@japan-actress.example
                </a>
              </li>
              <li className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 font-label text-xs text-foreground-800">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-map-pin-line" aria-hidden="true"></i>
                </span>
                东京都涩谷区 · 资料编辑室
              </li>
              <li className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 font-label text-xs text-foreground-800">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                每周三 10:00-18:00 集中整理
              </li>
            </ul>
            <div className="mt-4 flex items-center gap-2">
              <a
                href="https://hseb.com.cn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X 社交平台"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-twitter-x-line" aria-hidden="true"></i>
              </a>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-instagram-line" aria-hidden="true"></i>
              </a>
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-youtube-line" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="font-heading text-base font-bold text-accent-950">关于收藏功能</h2>
            <p className="mt-2 font-label text-xs leading-relaxed text-accent-900/80">
              女优详情页的收藏按钮为前端演示功能，状态保存在浏览器本地，不会上传服务器，也不与账户关联。清除浏览器数据后收藏会重置。
            </p>
            <Link
              to="/category"
              className="mt-3 inline-flex items-center gap-1 font-label text-xs text-accent-900 underline underline-offset-2"
            >
              去分类页体验收藏
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </aside>
      </section>

      <div className="mt-10 rounded-lg border border-background-200 bg-background-100 px-5 py-4 text-center">
        <p className="font-label text-[11px] leading-relaxed text-foreground-500">
          © 2026 日本女优 · 资料索引站。本站为示例资料展示站点，不提供视频播放、下载或交易服务。
        </p>
      </div>
    </SiteLayout>
  );
}